import { Box, Chip, InputAdornment, Stack, styled, Switch, TextField, Tooltip, Typography } from "@mui/material"
import Grid from "@mui/material/Grid2"
import * as React from "react"
import { SERVICE_ORDER_SERVICE_COLOR_MAP, SERVICE_ORDER_SERVICE_FRIENDLY_NAMES, SERVICE_ORDER_CUSTOMER_APPROVAL_FRIENDLY_NAMES, SERVICE_ORDER_CUSTOMER_APPROVAL_COLOR_MAP } from "../../Constants"
import FolderSpecialIcon from '@mui/icons-material/FolderSpecial';
import { moneyFormat, parseMoneyInput } from "../../Utils"
import AttachMoneyIcon from '@mui/icons-material/AttachMoney'
import InfoIcon from '@mui/icons-material/Info';
import Alert from '@mui/material/Alert';
import { useTrackedDarkMode, useTrackedServiceInputInMinutes } from "../UserState";
import dayjs from "dayjs"

const AntSwitch = styled(Switch)(({ theme }) => ({
    width: 28,
    height: 16,
    padding: 0,
    display: 'flex',
    '&:active': {
        '& .MuiSwitch-thumb': {
            width: 15,
        },
        '& .MuiSwitch-switchBase.Mui-checked': {
            transform: 'translateX(9px)',
        },
    },
    '& .MuiSwitch-switchBase': {
        padding: 2,
        '&.Mui-checked': {
            transform: 'translateX(12px)',
            color: '#fff',
            '& + .MuiSwitch-track': {
                opacity: 1,
                backgroundColor: '#1890ff',
                ...theme.applyStyles('dark', {
                    backgroundColor: '#177ddc',
                }),
            },
        },
    },
    '& .MuiSwitch-thumb': {
        boxShadow: '0 2px 4px 0 rgb(0 35 11 / 20%)',
        width: 12,
        height: 12,
        borderRadius: 6,
        transition: theme.transitions.create(['width'], {
            duration: 200,
        }),
    },
    '& .MuiSwitch-track': {
        borderRadius: 16 / 2,
        opacity: 1,
        backgroundColor: 'rgba(0,0,0,.25)',
        boxSizing: 'border-box',
        ...theme.applyStyles('dark', {
            backgroundColor: 'rgba(255,255,255,.35)',
        }),
    },
}));

export default function ServiceOrderQuoteInvoiceService({
    serviceOrder,
    service,
    calculatedCosts,
    setCalculatedCosts,
    isQuote,
    warrantiedServices,
    warrantiedParts
}) {
    const [darkMode,] = useTrackedDarkMode();
    const [inputInMinutes, setInputInMinutes] = useTrackedServiceInputInMinutes();
    const borderColor = darkMode ? '#FFF' : 'rgba(0, 0, 0, 0.87)'
    const calculate_total = (service) => {
        const calc_service = calculatedCosts.services[service.id]
        var total = 0
        total += parseFloat(calc_service.flat_rate)
        total += Math.round((parseFloat(calc_service.hourly_rate) * parseFloat(calc_service.minutes_worked) / (inputInMinutes ? 60 : 1)) * 100000) / 100000
        total += Object.keys(calc_service.parts).reduce((accumulator, item) => accumulator + Math.round(parseFloat(calc_service.parts[item].actual_cost) * parseFloat(calc_service.parts[item].quantity) * 100) / 100, 0)
        total -= Object.keys(warrantiedServices[service.id] ?? {}).reduce((accumulator, warranty_id) => accumulator + parseFloat(warrantiedServices[service.id][warranty_id].association.estimated_coverage), 0)
        total -= service_parts_warranty_total
        return total
    }

    var service_parts_warranty_total = 0
    service.service_order_parts.forEach((part) => {
        service_parts_warranty_total += Object.keys(warrantiedParts[part.id] ?? {}).reduce((accumulator, warranty_id) => accumulator + parseFloat(warrantiedParts[part.id][warranty_id].association.estimated_coverage), 0)
    })

    const updateHourlyRate = (e) => {
        calculatedCosts.services[service.id].hourly_rate = parseMoneyInput(calculatedCosts.services[service.id].hourly_rate, e.target.value, { allow_empty: true })
        setCalculatedCosts({ ...calculatedCosts })
    }
    const updateMinutes = (e) => {
        calculatedCosts.services[service.id].minutes_worked = parseMoneyInput(calculatedCosts.services[service.id].minutes_worked, e.target.value, { allow_empty: true, precision: 5 })
        setCalculatedCosts({ ...calculatedCosts })
    }

    const updateFlatRate = (e) => {
        calculatedCosts.services[service.id].flat_rate = parseMoneyInput(calculatedCosts.services[service.id].flat_rate, e.target.value, { allow_empty: true })
        setCalculatedCosts({ ...calculatedCosts })
    }

    const updatePart = (part_id) => (e) => {
        calculatedCosts.services[service.id].parts[part_id].actual_cost = parseMoneyInput(calculatedCosts.services[service.id].parts[part_id].actual_cost, e.target.value, { allow_empty: true })
        setCalculatedCosts({ ...calculatedCosts })
    }

    return (
        (<Box key={service.id}>
            <Grid container sx={{ mb: 3 }} columns={{ sm: 6, md: 12 }} rowSpacing={1}>
                <Grid size={6}>
                    <h2>
                        <b>{service.name}</b>
                        {service.id in warrantiedServices &&
                            <Tooltip title={`Service has warranty claim`}>
                                <FolderSpecialIcon sx={{ height: '100%', verticalAlign: 'middle' }} />
                            </Tooltip>
                        }
                    </h2>
                    <Grid container spacing={1}>
                        <Grid size="auto">
                            <Box sx={{ height: '34px', verticalAlign: 'middle' }}>
                                <span style={{ lineHeight: '34px' }}>Status:</span>
                            </Box>
                            <Box sx={{ height: '34px', verticalAlign: 'middle' }}>
                                <span style={{ lineHeight: '34px' }}>Approval:</span>
                            </Box>
                        </Grid>
                        <Grid>
                            <Box sx={{ height: '34px', verticalAlign: 'middle' }}>
                                {!['COMPLETED', 'DECLINED'].includes(service.status) && !isQuote &&
                                    <Tooltip title={`Service not completed`}>
                                        <InfoIcon sx={{ height: '100%', verticalAlign: 'middle' }} color="error" />
                                    </Tooltip>
                                }
                                <Chip label={SERVICE_ORDER_SERVICE_FRIENDLY_NAMES[service.status]} color={SERVICE_ORDER_SERVICE_COLOR_MAP[service.status] || 'primary'} />
                            </Box>
                            <Box sx={{ height: '34px', verticalAlign: 'middle' }}>
                                <Chip label={SERVICE_ORDER_CUSTOMER_APPROVAL_FRIENDLY_NAMES[service.customer_approved_status]} color={SERVICE_ORDER_CUSTOMER_APPROVAL_COLOR_MAP[service.customer_approved_status] || 'primary'} />
                            </Box>
                        </Grid>
                    </Grid>
                </Grid>
                <Grid size={6}>
                    <h4 style={{ marginBottom: 12 }}>Service Summary Notes (Customer Visible)</h4>
                    <TextField
                        margin="dense"
                        id="summarynotes"
                        type="text"
                        placeholder="Overview of Service Work"
                        fullWidth
                        variant="filled"
                        multiline
                        minRows={3}
                        value={calculatedCosts.services[service.id]["summary_notes"]}
                        onChange={(event) => {
                            calculatedCosts.services[service.id]["summary_notes"] = event.target.value
                            setCalculatedCosts({ ...calculatedCosts })
                        }}
                        slotProps={{
                            inputLabel: { shrink: false },
                            input: { maxLength: 2055, style: { paddingTop: 12 } }
                        }}
                        sx={{ mt: 0 }}
                    />
                </Grid>
            </Grid>
            <Grid container rowSpacing={1} sx={{ border: `1px solid ${borderColor}`, borderRadius: '16px' }}>
                <Grid container sx={{ borderBottom: `1px solid ${borderColor}` }} size={12}>
                    <Grid sx={{ pl: 2 }} size={2}>
                        <span>Type</span>
                    </Grid>
                    <Grid size={3}>
                        <span>Rate</span>
                    </Grid>
                    <Grid size={3}>
                        <span>Details</span>
                    </Grid>
                    <Grid sx={{ textAlign: 'right' }} size={2}>
                        <span>Quantity</span>
                    </Grid>
                    <Grid sx={{ textAlign: 'right', pr: 4 }} size={2}>
                        <span>Total</span>
                    </Grid>
                </Grid>
                <Grid container alignItems={'center'} size={12}>
                    {/* FLAT RATE ROW */}
                    <Grid sx={{ pl: 2 }} size={2}>
                        <span>Flat Rate</span>
                    </Grid>
                    <Grid size={3}>
                        <TextField
                            margin="dense"
                            id="flat_rate"
                            label="Flat Rate Cost"
                            type="number"
                            inputMode="numeric"
                            size="small"
                            value={calculatedCosts.services[service.id].flat_rate.toString()}
                            onChange={updateFlatRate}
                            disabled={service.status === 'DECLINED'}
                            helperText={!isQuote && `Quoted: ${service.quoted_flat_rate ? moneyFormat(service.quoted_flat_rate) : 'N/A'}`}
                            slotProps={{
                                input: {
                                    startAdornment: <InputAdornment position="start">
                                        {<AttachMoneyIcon />}
                                    </InputAdornment>,
                                },
                            }}
                            sx={{ maxWidth: '255px' }}
                        />
                    </Grid>
                    <Grid size={3}>
                    </Grid>
                    <Grid sx={{ textAlign: 'right' }} size={2}>
                        <span>1</span>
                    </Grid>
                    <Grid sx={{ textAlign: 'right', pr: 4 }} size={2}>
                        <span>${moneyFormat(calculatedCosts.services[service.id].flat_rate)}</span>
                    </Grid>
                </Grid>
                <Grid container alignItems={'center'} sx={{ borderTop: `1px solid ${borderColor}` }} size={12}>
                    {/* HOURLY RATE ROW */}
                    <Grid sx={{ pl: 2 }} size={2}>
                        <span>Hourly Rate</span>
                    </Grid>
                    <Grid size={3}>
                        <TextField
                            margin="dense"
                            id="hourly_rate"
                            label="Hourly Rate"
                            type="number"
                            inputMode="numeric"
                            size="small"
                            value={calculatedCosts.services[service.id].hourly_rate.toString()}
                            onChange={updateHourlyRate}
                            disabled={service.status === 'DECLINED'}
                            helperText={!isQuote && `Quoted: ${service.quoted_hourly_rate ? moneyFormat(service.quoted_hourly_rate) : 'N/A'}`}
                            slotProps={{
                                input: {
                                    startAdornment: <InputAdornment position="start">
                                        {<AttachMoneyIcon />}
                                    </InputAdornment>,
                                    endAdornment: <InputAdornment position="end">/ hr</InputAdornment>,
                                },
                            }}
                            sx={{ maxWidth: '255px' }}
                        />
                    </Grid>
                    <Grid container sx={{ textAlign: 'right' }} size={5} justifyContent={'flex-end'} alignItems={'center'} spacing={1}>
                        <Grid size="auto">
                            <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                                <Typography>Hr</Typography>
                                <AntSwitch
                                    inputProps={{ 'aria-label': 'ant design' }}
                                    onChange={(e) => {
                                        localStorage.setItem(`QuoteInMinutes`, e.target.checked)
                                        setInputInMinutes(e.target.checked)
                                        Object.values(calculatedCosts.services).forEach((s) => {
                                            if (e.target.checked) {
                                                s.minutes_worked = Math.round(parseFloat(s.minutes_worked) * 60 * 100000) / 100000
                                            } else {
                                                s.minutes_worked = Math.round((parseFloat(s.minutes_worked) / 60) * 100000) / 100000
                                            }
                                        })
                                        setCalculatedCosts({ ...calculatedCosts })
                                    }}
                                    checked={inputInMinutes}
                                />
                                <Typography>Min</Typography>
                            </Stack>
                        </Grid>
                        <Grid size="auto">
                            <TextField
                                margin="dense"
                                id="minutes"
                                label={inputInMinutes ? "Minutes" : "Hours"}
                                type="number"
                                inputMode="numeric"
                                size="small"
                                value={calculatedCosts.services[service.id].minutes_worked.toString()}
                                onChange={updateMinutes}
                                disabled={service.status === 'DECLINED'}
                                helperText={!isQuote && `Quoted: ${service.quoted_minutes_worked ? (inputInMinutes ? service.quoted_minutes_worked.toString() : (Math.round((service.quoted_minutes_worked / 60) * 100000) / 100000).toString()) : 'N/A'}`}
                                sx={{ maxWidth: '130px' }}
                            />
                        </Grid>
                    </Grid>
                    <Grid sx={{ textAlign: 'right', pr: 4 }} size={2}>
                        <span>${moneyFormat(parseFloat(calculatedCosts.services[service.id].hourly_rate) * parseFloat(calculatedCosts.services[service.id].minutes_worked) / (inputInMinutes ? 60 : 1))}</span>
                    </Grid>
                </Grid>
                {Object.keys(warrantiedServices[service.id] ?? {}).map((warranty_id) =>
                    <Grid container alignItems={'center'} sx={{ borderTop: `1px solid ${borderColor}` }} size={12} key={warranty_id}>
                        {/* SERVICE WARRANTY ROW */}
                        <Grid sx={{ pl: 2 }} size={5}>
                            <span>Service Warranty</span>
                        </Grid>
                        <Grid size={3}>
                            {parseFloat(warrantiedServices[service.id][warranty_id].association.estimated_coverage) !== parseFloat(calculatedCosts.services[service.id].flat_rate) &&
                                <Alert severity="warning">Partial Warranty Coverage</Alert>
                            }
                        </Grid>
                        <Grid sx={{ textAlign: 'right', pr: 4 }} size={4}>
                            <span>-${moneyFormat(warrantiedServices[service.id][warranty_id].association.estimated_coverage)}</span>
                        </Grid>
                    </Grid>
                )}
                {service.service_order_parts.length > 0 &&
                    <Grid container alignItems={'center'} sx={{ borderTop: `1px solid ${borderColor}` }} size={12}>
                        {/* PARTS ROW */}
                        <Grid sx={{ pl: 2 }} size={2}>
                            <span>Parts</span>
                        </Grid>
                        <Grid size={3}>
                            <span><i>{service.part_price_adjustment === 0 ? `At Cost` : `${service.part_price_adjustment > 0 ? '+' : ''}${service.part_price_adjustment}%`}</i></span>
                        </Grid>
                        <Grid size={7}>
                            {/* EMPTY */}
                        </Grid>
                        {service.service_order_parts.sort((a, b) => dayjs.utc(a.date_created).unix() - dayjs.utc(b.date_created).unix()).map((part) =>
                            <React.Fragment key={part.id}>
                                <Grid container alignItems={'center'} size={12}>
                                    <Grid container alignItems={'center'} spacing={1} size={9}>
                                        <Grid size={8} sx={{ textAlign: 'right' }}>
                                            {!['INSTALLED', 'DECLINED'].includes(part.status_type) && !isQuote &&
                                                <Tooltip title={`Part not installed`}>
                                                    <InfoIcon sx={{ height: '100%', verticalAlign: 'middle' }} color="error" />
                                                </Tooltip>
                                            }
                                            <span>{part.name}</span>
                                            <span>{part.status_type === 'DECLINED' ? ` (${part.status_name})` : ''}</span>
                                            <span><i>{part.sku ? ` (${part.sku})` : ''}</i></span>
                                        </Grid>
                                        <Grid size={4}>
                                            <TextField
                                                margin="dense"
                                                id={`${part.id}-price`}
                                                label="Price"
                                                type="number"
                                                inputMode="numeric"
                                                size="small"
                                                value={calculatedCosts.services[service.id].parts[part.id].actual_cost.toString()}
                                                onChange={updatePart(part.id)}
                                                disabled={['DECLINED'].includes(part.status_type)}
                                                helperText={!isQuote ?
                                                    `Quoted: ${part.quoted_cost ? `${moneyFormat(part.quoted_cost)} x ${part.quoted_quantity}` : 'N/A'}`
                                                    :
                                                    `Retail: ${moneyFormat(part.cost)}`}
                                                slotProps={{
                                                    input: {
                                                        startAdornment: (
                                                            <InputAdornment position="start">
                                                                {<AttachMoneyIcon />}
                                                            </InputAdornment>
                                                        ),
                                                    }
                                                }}
                                            />
                                        </Grid>
                                    </Grid>
                                    <Grid sx={{ textAlign: 'right' }} size={1}>
                                        <span>{part.quantity}</span>
                                    </Grid>
                                    <Grid sx={{ textAlign: 'right', pr: 4 }} size={2}>
                                        <span>${moneyFormat(parseFloat(calculatedCosts.services[service.id].parts[part.id].actual_cost) * parseFloat(part.quantity))}</span>
                                    </Grid>
                                </Grid>
                                {Object.keys(warrantiedParts[part.id] ?? {}).map((warranty_id) =>
                                    <Grid container alignItems={'center'} size={12} key={warranty_id}>
                                        {/* PART WARRANTY ROW */}
                                        <Grid sx={{ pl: 2 }} size={5}>
                                            <Tooltip title={`#${warrantiedParts[part.id][warranty_id].warranty.claim_number}`} arrow>
                                                <span>Part Warranty</span>
                                            </Tooltip>
                                        </Grid>
                                        <Grid size={3}>
                                            {parseFloat(warrantiedParts[part.id][warranty_id].association.estimated_coverage) !== parseFloat(calculatedCosts.services[service.id].parts[part.id].actual_cost) &&
                                                <Alert severity="warning">Partial Warranty Coverage</Alert>
                                            }
                                        </Grid>
                                        <Grid sx={{ textAlign: 'right', pr: 4 }} size={4}>
                                            <span>-${moneyFormat(warrantiedParts[part.id][warranty_id].association.estimated_coverage)}</span>
                                        </Grid>
                                    </Grid>
                                )}
                            </React.Fragment>
                        )}
                        {service_parts_warranty_total > 0 &&
                            <Grid container alignItems={'center'} sx={{ borderTop: `1px solid ${borderColor}`, pb: 1 }} size={12}>
                                {/* PARTS WARRANTY ROW */}
                                <Grid sx={{ pl: 2 }} size={8}>
                                    <span>Parts Warranty</span>
                                </Grid>
                                <Grid sx={{ textAlign: 'right', pr: 4 }} size={4}>
                                    <span>-${moneyFormat(service_parts_warranty_total)}</span>
                                </Grid>
                            </Grid>
                        }
                        <Grid sx={{ textAlign: 'right', pr: 4 }} size={12}>
                            <span>${moneyFormat(Object.values(calculatedCosts.services[service.id].parts).reduce((partialSum, a) => partialSum + (parseFloat(a.actual_cost) * parseFloat(a.quantity)), 0))}</span>
                        </Grid>
                    </Grid>
                }
                <Grid container alignItems={'center'} sx={{ borderTop: `1px solid ${borderColor}`, pb: 1 }} size={12}>
                    {/* TOTAL ROW */}
                    <Grid sx={{ pl: 2 }} size={10}>
                        <span>Total</span>
                    </Grid>
                    <Grid sx={{ textAlign: 'right', pr: 4 }} size={2}>
                        <span><b>${`${moneyFormat(calculate_total(service))}`}</b></span>
                    </Grid>
                </Grid>
            </Grid>
        </Box >)
    );
}